import type * as React from 'react';

import { flex, spacing } from '../constants';
import { NonEditableView } from '../DragAndDrop';
import type { InteractiveState } from '../Interactive';
import { ThemedStyleSheet, themedFonts, useCurrentTheme } from '../theme';
import { normalizeStyle, type ViewStyle } from '../web';
import { type CodeBlockLineHighlighting, highlightingThemedStyles } from './line-highlighting';

export interface CodeBlockLineNumberProps {
    number: number;
    highlighting?: CodeBlockLineHighlighting;
    onPress?: () => void;
    gutterInteractiveState: InteractiveState;
}

export const CODEBLOCK_LINE_GUTTER_WIDTH = 48;

/**
 * Gutter for a code block line with the number.
 */
export function CodeBlockLineNumber(props: CodeBlockLineNumberProps): React.ReactElement {
    const { number, highlighting, gutterInteractiveState, onPress } = props;

    const [, font] = useCurrentTheme(themedFonts);
    const [, styles] = useCurrentTheme(themedStyles);
    const [, highlightingStyles] = useCurrentTheme(
        // @ts-ignore
        highlighting ? highlightingThemedStyles[highlighting] : undefined
    );

    const textContent = (
        <span
            style={normalizeStyle([
                font.Code,
                gutterInteractiveState.hovered ? font.text : font.textMuted,
                font.AlignRight,
                highlightingStyles?.text,
            ])}
        >
            {number}
        </span>
    );

    const contentStyles = [styles.lineNumberContent, highlightingStyles?.line];
    const content = onPress ? (
        <button
            type="button"
            className="flex w-full appearance-none border-0 bg-transparent p-0"
            onClick={onPress}
            style={normalizeStyle(contentStyles)}
            {...gutterInteractiveState.domEvents}
        >
            {textContent}
        </button>
    ) : (
        textContent
    );

    return (
        <NonEditableView style={[styles.lineNumberWrapper, onPress ? flex.row : contentStyles]}>
            {content}
        </NonEditableView>
    );
}

const themedStyles = ThemedStyleSheet.create({
    lineNumberWrapper: (theme) =>
        // @ts-ignore position 'sticky' isn't a valid value
        ({
            flexGrow: 0,
            flexShrink: 0,
            width: CODEBLOCK_LINE_GUTTER_WIDTH,
            // fix the line numbers on the left when scrolling
            // and make sure the text goes under them
            position: 'sticky',
            left: 0,
            backgroundColor: theme.colors.background.muted,
            zIndex: 1,
        }) as ViewStyle,
    lineNumberContent: {
        width: CODEBLOCK_LINE_GUTTER_WIDTH,
        paddingRight: spacing.small,
    },
});
